import React, { useContext, useEffect, useState, useRef } from "react";
import LoadingIcon from "../../images/Loading_icon.gif";
import { UrlContext } from "../../contexts/UrlContext";
import { motion, AnimatePresence } from "framer-motion";
import ContactLogRow from "../ProjectDetails/ContactLogRow";
import ReadOnlyEmailViewer from "../ProjectDetails/ReadOnlyEmailViewer";
import { DateTime } from "luxon";

export default function ContactLog({ expertID, firstContactLog, showStickyHeader }) {
  const [contactLog, setContactLog] = useState(null);
  const [showContactLog, setShowContactLog] = useState(false);
  const dropDownRef = useRef(null);
  const emailPanelRef = useRef(null);
  const [logloading, setLogLoading] = useState(false);
  const { API_URL } = useContext(UrlContext);
  const [selectedLog, setSelectedLog] = useState(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [logPage, setLogPage] = useState(1);
  const logsPerPage = 5;
  const totalPages = Math.ceil((contactLog?.length || 0) / logsPerPage);
  const currentLogs = (contactLog || []).slice((logPage - 1) * logsPerPage, logPage * logsPerPage);

  // Api call to get the full contact log
  const fetchContactLog = () => {
    setShowContactLog(true);
    setLogLoading(true);
    const url = `${API_URL}ex-contact-log/` + expertID;
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setLogLoading(false);
        setContactLog(data.contactLog);
        setLogPage(1);
      })
      .catch((err) => {
        console.log(err);
        setLogLoading(false);
      });
  };  

    // Api call to refresh the contact log
  const refreshContactLog = () => {
    setIsRefreshing(true);
    const url = `${API_URL}refresh-outlook-eamil-log`;
    const data = {
      expertID: expertID,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
    .then((res) => res.json())
    .then((data) => {
      setIsRefreshing(false);
      if (data.status === "success") {
        fetchContactLog();
      }
      else {
        console.log("Failed to refresh contact log");
      }
    })
    .catch((err) => {
      setIsRefreshing(false);
      console.log(err);
    });
  };

  const formatTime = (input, timeZoneStr = "UTC") => {
    try {
      let dt;
  
      if (input instanceof Date) {
        dt = DateTime.fromJSDate(input, { zone: timeZoneStr });
      } else if (typeof input === "string") {
        // First try HTTP-style date (e.g., from Flask)
        dt = DateTime.fromHTTP(input, { zone: timeZoneStr });
  
        // If that fails, fallback to ISO
        if (!dt.isValid) {
          dt = DateTime.fromISO(input, { zone: timeZoneStr });
        }
      } else {
        return "";
      }
  
      if (!dt.isValid) return "";
  
      return dt.toLocal().toFormat("cccc, d LLL yyyy, HH:mm");
    } catch {
      return "";
    }
  };

  // This effect is used to disable scrolling when the contact log is open
  useEffect(() => {
    if (showContactLog) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  
    return () => {
      document.body.style.overflow = "";
    };
  }, [showContactLog]); 

  return (
    <>
      {firstContactLog ? (
        <>
          <div className="pjcon-contact-log" style={{marginRight: "10px"}}>
            {/* 🔥 Slide-in Contact Log Panel */}
            <AnimatePresence>
              {showContactLog && (
                <>
                  {/* Dimmed background */}
                  <motion.div
                    className="fixed inset-0 bg-black/30 z-30"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => {
                      if (selectedLog) {
                        // Only close email panel first
                        setSelectedLog(null);
                      } else {
                        // If no email panel, then close contact log
                        setShowContactLog(false);
                      }
                    }}
                  />
                  <motion.div
                    className="fixed top-[78px] right-0 h-full w-[400px] bg-white shadow-xl z-40 pt-2 pl-4 pr-4 pb-4 flex flex-col"
                    ref={dropDownRef}
                    initial={{ x: "100%" }}
                    animate={{ x: selectedLog?.isOutlook ? -800 : 0 }}
                    exit={{ x: "100%" }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  >
                    {showStickyHeader && (
                      <div className="mt-[60px]"></div>
                    )}
                    <div className="flex justify-between items-center mb-2">
                      <button
                        className="text-gray-500 hover:text-black hover:bg-gray-200 focus:outline-none pt-0.5 pb-0.5 pl-2 pr-2 rounded-full transition"
                        onClick={() => setShowContactLog(false)}
                      >
                        <i className="fa-solid fa-xmark text-lg"></i>
                      </button>
                      <div className="group relative flex items-center justify-center w-fit">
                        {isRefreshing ? (
                          <i className="fa-solid fa-arrows-rotate fa-spin text-[#365561] text-lg cursor-pointer" />
                        ) : (
                          <i className="fa-solid fa-arrows-rotate text-[#365561] text-lg cursor-pointer" onClick={() => refreshContactLog()} />
                        )}

                        {/* Tooltip on hover */}
                        <div className="absolute top-full mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 pointer-events-none">
                          Refresh log
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 mb-4">

                      {/* Name and Title */}
                      <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-gray-800">Contact Log</h2>
                      </div>
                    </div>

                    {/* Scrollable area */}
                    <div className="flex-1 overflow-y-auto">
                      {logloading ? (
                        <div className="flex items-center justify-center py-6">
                          <img src={LoadingIcon} alt="Loading..." className="h-5 w-5 animate-spin" />
                          <span className="ml-2 text-sm text-gray-500">Loading contact log...</span>
                        </div>
                      ) : contactLog && contactLog.length > 0 ? (
                        // [...contactLog]
                        [...currentLogs]
                          .sort((a, b) => new Date(b.emailDate || b.dateContacted) - new Date(a.emailDate || a.dateContacted))
                          .map((log, index) => (
                            <ContactLogRow
                              key={index}
                              log={log}
                              onSelect={() =>
                                selectedLog === log ? setSelectedLog(null) : setSelectedLog(log)
                              }
                              selected={log === selectedLog}
                            />
                          ))
                      ) : (
                        <p className="text-gray-500 text-sm">No information available yet!</p>
                      )}
                    </div>

                    {/* Load More button fixed to bottom */}
                    <div className="pt-2">
                      <div className="flex justify-between items-center">
                        {logPage > 1 ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setLogPage((prev) => Math.max(prev - 1, 1))}
                              className="text-gray-500 bg-gray-200 hover:text-white hover:bg-[#365561] focus:outline-none px-[10px] py-1 rounded-full transition"
                            >
                              <i class="fa-solid fa-chevron-left"></i>
                            </button>
                            <p className="text-sm text-gray-500">Previous</p>
                          </div>
                        ) : <div></div>}

                        {logPage < totalPages && (
                          <div className="flex items-center gap-1">
                            <p className="text-sm text-gray-500">Next</p>
                            <button
                              onClick={() => setLogPage((prev) => Math.min(prev + 1, totalPages))}
                              className="text-gray-500 bg-gray-200 hover:text-white hover:bg-[#365561] focus:outline-none px-[10px] py-1 rounded-full transition"
                            >
                              <i class="fa-solid fa-chevron-right"></i>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mb-[70px]"></div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>

            {/* 🔥 Slide-in Outlook Email Viewer – separate from log panel */}
            <AnimatePresence>
              {selectedLog && selectedLog.isOutlook && (
                <motion.div
                ref={emailPanelRef}
                key="email-viewer"
                className="fixed top-[78px] right-0 h-[calc(100%-60px)] w-[800px] bg-white shadow-2xl z-50 p-4 overflow-y-auto border-l"
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-b from-black/5 to-transparent pointer-events-none z-10" />
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">Outlook Email</h3>
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // optional safety
                      setSelectedLog(null);
                    }}
                    className="text-gray-600 hover:text-gray-800"
                  >
                    <i className="fa-solid fa-xmark text-xl" />
                  </button>
                </div>
                <ReadOnlyEmailViewer email={JSON.parse(selectedLog.emailObject)} />
              </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* First contact details */}
          <motion.div
            className="pjcon-contacted-by relative cursor-pointer"
            // style={{ color: "#365561" }}
            // whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            onClick={() => {
              setLogLoading(true);
              fetchContactLog();
            }}
            onMouseEnter={() => setTooltipOpen(true)}
            onMouseLeave={() => setTooltipOpen(false)}
          >
            {/* Tooltip (with Framer Motion) */}
            <AnimatePresence>
              {tooltipOpen && (
                <motion.div
                  className="absolute -top-7 left-1/4 -translate-x-1/2 bg-[#365561] text-white text-xs px-2 py-1 rounded shadow-md whitespace-nowrap z-50 pointer-events-none"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                >
                  Click to view call log
                </motion.div>
              )}
            </AnimatePresence>

            {firstContactLog ? (
              <>
                <p>✉️ Last contacted by {firstContactLog.contactedBy} on {formatTime(JSON.parse(firstContactLog.emailObject || "{}")?.receivedDateTime || JSON.parse(firstContactLog.emailObject || "{}")?.sentDateTime || firstContactLog.dateContacted)}</p>
              </>
            ) : (
              <p>No contact log available!</p>
            )}
          </motion.div>
        </>
      ) : (
        null
      )}
    </>
  );
}